import React from 'react';
import { Box, Grid, Paper, Typography, TextField, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stack, Select, MenuItem } from '@mui/material';

export function getDefaultRefractValues() {
  return {
    lensometry: {
      ageOfGlasses: '',
      od: { sphere: '', cyl: '', axis: '', prism: '', add: '' },
      os: { sphere: '', cyl: '', axis: '', prism: '', add: '' },
    },
    autoRefractor: {
      od: { sphere: '', cyl: '', axis: '', acuity: '', pd: '' },
      os: { sphere: '', cyl: '', axis: '', acuity: '', pd: '' },
    },
    staticRetinoscopy: {
      od: { sphere: '', cyl: '', axis: '', acuity: '' },
      os: { sphere: '', cyl: '', axis: '', acuity: '' },
    },
    subjective: {
      method: '', // '', 'Snellen', 'Isolated Pictures', 'Numbers'
      od: { sphere: '', cyl: '', axis: '', acuity: '' },
      os: { sphere: '', cyl: '', axis: '', acuity: '' },
    },
    subjectiveAdd: {
      od: { add: '', acuity: '' },
      os: { add: '', acuity: '' },
    },
    balance: {
      method: '',
      od: { sphere: '', cyl: '', axis: '', acuity: '' },
      os: { sphere: '', cyl: '', axis: '', acuity: '' },
    },
    balanceAdd: {
      od: { add: '', acuity: '' },
      os: { add: '', acuity: '' },
    },
    cycloplegic: {
      od: { sphere: '', cyl: '', axis: '', acuity: '' },
      os: { sphere: '', cyl: '', axis: '', acuity: '' },
    },
    keratometry: { od: '', os: '' },
  };
}

function TextCell({ label, value, onChange, width }) {
  return (
    <TextField
      variant="outlined"
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ 'aria-label': label }}
      sx={width ? { maxWidth: width } : undefined}
    />
  );
}

function TwoRowTable({ columns, rows, data, onChange }) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }} />
            {columns.map(col => (
              <TableCell key={col} sx={{ fontWeight: 600 }}>{col}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(side => (
            <TableRow key={side}>
              <TableCell sx={{ fontWeight: 600, width: 52 }}>{side}</TableCell>
              {columns.map(col => (
                <TableCell key={`${side}-${col}`}>
                  <TextCell
                    label={`${side} ${col}`}
                    value={data?.[side.toLowerCase()]?.[col.toLowerCase()] || ''}
                    onChange={(val) => onChange(side.toLowerCase(), col.toLowerCase(), val)}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function RefractTab({ value, onChange, onCopyToDiagnosisRx }) {
  const v = value || getDefaultRefractValues();

  const update = (path, val) => {
    const next = { ...v };
    // simple path setter: path like ['lensometry','ageOfGlasses']
    let ref = next;
    for (let i = 0; i < path.length - 1; i++) {
      const k = path[i];
      ref[k] = { ...ref[k] };
      ref = ref[k];
    }
    ref[path[path.length - 1]] = val;
    onChange?.(next);
  };

  const updateTable = (section, side, field, val) => {
    const sec = { ...v[section], [side]: { ...v[section]?.[side], [field]: val } };
    onChange?.({ ...v, [section]: sec });
  };

  const onFinalClick = (source) => {
    // Build a patch for DiagnosisTab rx columns from the chosen source section
    if (!onCopyToDiagnosisRx) return;

    const lower = (s) => (s || '').toString().trim().toLowerCase();
    const patch = {};

    const mapSphereCylAxis = (section) => {
      const sec = v[section] || {};
      const od = sec.od || {};
      const os = sec.os || {};
      patch['Sphere'] = { od: od.sphere || '', os: os.sphere || '' };
      // source uses "cyl" while diagnosis uses "Cylinder"
      patch['Cylinder'] = { od: od.cyl || '', os: os.cyl || '' };
      patch['Axis'] = { od: od.axis || '', os: os.axis || '' };
    };

    switch (lower(source)) {
      case 'lensometry': {
        const sec = v.lensometry || {};
        const od = sec.od || {};
        const os = sec.os || {};
        patch['Sphere'] = { od: od.sphere || '', os: os.sphere || '' };
        patch['Cylinder'] = { od: od.cyl || '', os: os.cyl || '' };
        patch['Axis'] = { od: od.axis || '', os: os.axis || '' };
        patch['Prism'] = { od: od.prism || '', os: os.prism || '' };
        patch['Add'] = { od: od.add || '', os: os.add || '' };
        break;
      }
      case 'autorefractor': {
        mapSphereCylAxis('autoRefractor');
        break;
      }
      case 'staticretinoscopy': {
        mapSphereCylAxis('staticRetinoscopy');
        break;
      }
      case 'subjectiveadd': {
        const sec = v.subjectiveAdd || {};
        const od = sec.od || {};
        const os = sec.os || {};
        patch['Add'] = { od: od.add || '', os: os.add || '' };
        break;
      }
      case 'balanceadd': {
        const sec = v.balanceAdd || {};
        const od = sec.od || {};
        const os = sec.os || {};
        patch['Add'] = { od: od.add || '', os: os.add || '' };
        break;
      }
      case 'subjective': {
        mapSphereCylAxis('subjective');
        break;
      }
      case 'balance': {
        mapSphereCylAxis('balance');
        break;
      }
      case 'cycloplegic': {
        mapSphereCylAxis('cycloplegic');
        break;
      }
      default:
        return;
    }

    onCopyToDiagnosisRx(patch);
  };

  const onPrintRx = (source) => {
    // Placeholder
    console.debug('Print Rx requested for', source);
  };

  const methodOptions = ['', 'Snellen', 'Isolated Pictures', 'Numbers'];

  const copyFromSubjectiveToBalance = () => {
    const next = { ...v };
    next.balance = {
      method: v.subjective.method,
      od: { ...v.subjective.od },
      os: { ...v.subjective.os },
    };
    next.balanceAdd = {
      od: { ...v.subjectiveAdd.od },
      os: { ...v.subjectiveAdd.os },
    };
    onChange?.(next);
  };

  return (
    <Box>
      {/* Row 1 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Lensometry</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" sx={{ minWidth: 120 }}>Age of glasses</Typography>
              <TextField
                variant="outlined"
                size="small"
                value={v.lensometry.ageOfGlasses}
                onChange={(e) => update(['lensometry', 'ageOfGlasses'], e.target.value)}
                placeholder="e.g., 2 years"
              />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Lensometry</Typography>
            <TwoRowTable
              columns={["Sphere", "Cyl", "Axis", "Prism", "Add"]}
              rows={["OD", "OS"]}
              data={v.lensometry}
              onChange={(side, field, val) => updateTable('lensometry', side, field, val)}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Row 2 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Auto Refractor</Typography>
              <Button size="small" variant="outlined" onClick={() => onFinalClick('autoRefractor')}>Final</Button>
            </Box>
            <TwoRowTable
              columns={["Sphere", "Cyl", "Axis", "Acuity", "PD"]}
              rows={["OD", "OS"]}
              data={v.autoRefractor}
              onChange={(side, field, val) => updateTable('autoRefractor', side, field, val)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Static Retinoscopy</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => onPrintRx('staticRetinoscopy')}>Print Rx</Button>
                <Button size="small" variant="outlined" onClick={() => onFinalClick('staticRetinoscopy')}>Final</Button>
                {/* TODO: third button requested but unspecified */}
              </Stack>
            </Box>
            <TwoRowTable
              columns={["Sphere", "Cyl", "Axis", "Acuity"]}
              rows={["OD", "OS"]}
              data={v.staticRetinoscopy}
              onChange={(side, field, val) => updateTable('staticRetinoscopy', side, field, val)}
            />
          </Paper>
        </Grid>
      </Grid>


      {/* Row 3 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Subjective</Typography>
              <Select
                variant="outlined"
                size="small"
                value={v.subjective.method || ''}
                onChange={(e) => update(['subjective','method'], e.target.value)}
                displayEmpty
                sx={{ minWidth: 180 }}
              >
                <MenuItem value=""><em>Method</em></MenuItem>
                {methodOptions.filter(o => o).map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </Box>
            <TwoRowTable
              columns={["Sphere", "Cyl", "Axis", "Acuity"]}
              rows={["OD", "OS"]}
              data={v.subjective}
              onChange={(side, field, val) => updateTable('subjective', side, field, val)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Subjective</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => onPrintRx('subjectiveAdd')}>Print</Button>
                <Button size="small" variant="outlined" onClick={() => onFinalClick('subjectiveAdd')}>Final</Button>
              </Stack>
            </Box>
            <TwoRowTable
              columns={["Add", "Acuity"]}
              rows={["OD", "OS"]}
              data={v.subjectiveAdd}
              onChange={(side, field, val) => updateTable('subjectiveAdd', side, field, val)}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Row 4 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Balance</Typography>
              <Select
                variant="outlined"
                size="small"
                value={v.balance.method || ''}
                onChange={(e) => update(['balance','method'], e.target.value)}
                displayEmpty
                sx={{ minWidth: 180 }}
              >
                <MenuItem value=""><em>Method</em></MenuItem>
                {methodOptions.filter(o => o).map(opt => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </Box>
            <TwoRowTable
              columns={["Sphere", "Cyl", "Axis", "Acuity"]}
              rows={["OD", "OS"]}
              data={v.balance}
              onChange={(side, field, val) => updateTable('balance', side, field, val)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Balance</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => onPrintRx('balanceAdd')}>Print</Button>
                <Button size="small" variant="outlined" onClick={() => onFinalClick('balanceAdd')}>Final</Button>
                <Button size="small" variant="outlined" onClick={copyFromSubjectiveToBalance}>Copy</Button>
              </Stack>
            </Box>
            <TwoRowTable
              columns={["Add", "Acuity"]}
              rows={["OD", "OS"]}
              data={v.balanceAdd}
              onChange={(side, field, val) => updateTable('balanceAdd', side, field, val)}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Row 5 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Cycloplegic</Typography>
            <TwoRowTable
              columns={["Sphere", "Cyl", "Axis", "Acuity"]}
              rows={["OD", "OS"]}
              data={v.cycloplegic}
              onChange={(side, field, val) => updateTable('cycloplegic', side, field, val)}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Keratometry</Typography>
            <Stack direction="row" spacing={2}>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>OD</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  value={v.keratometry.od}
                  onChange={(e) => update(['keratometry','od'], e.target.value)}
                  placeholder="OD"
                />
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>OS</Typography>
                <TextField
                  variant="outlined"
                  size="small"
                  value={v.keratometry.os}
                  onChange={(e) => update(['keratometry','os'], e.target.value)}
                  placeholder="OS"
                />
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
